import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../css/contactus.scss";

const initialFormData = {
  name: "",
  email: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isReady, setIsReady] = useState(false);

  const emailHref = useMemo(() => {
    const subject = encodeURIComponent(
      `ioomparade website inquiry from ${formData.name.trim() || "a customer"}`
    );
    const body = encodeURIComponent(
      `Name: ${formData.name.trim()}\nEmail: ${formData.email.trim()}\n\n${formData.message.trim()}`
    );

    return `mailto:globe@ioomparade.online?subject=${subject}&body=${body}`;
  }, [formData]);

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) {
      nextErrors.name = "Please enter your name.";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add a little more detail to your message.";
    }

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setIsReady(false);

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: undefined }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setIsReady(false);
      const firstInvalidField = document.getElementById(
        `contact-${Object.keys(nextErrors)[0]}`
      );
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setIsReady(true);
  };

  const renderFieldError = (field) =>
    errors[field] ? (
      <p className="contact-form__error" id={`contact-${field}-error`}>
        {errors[field]}
      </p>
    ) : null;

  return (
    <main className="ink-info-page ink-info-page--contact">
      <div className="ink-info-page__inner">
        <header className="ink-info-page__header">
          <p className="ink-info-page__eyebrow">Let&apos;s talk pens</p>
          <h1>Contact</h1>
          <p className="ink-info-page__lead">
            Email is the quickest way to reach ioomparade. Share your question
            below, or check the common delivery and return answers first.
          </p>
        </header>

        <div className="contact-layout">
          <aside className="contact-details" aria-labelledby="contact-details-title">
            <p className="ink-info-page__kicker">Get in touch</p>
            <h2 id="contact-details-title">We&apos;d love to hear from you</h2>
            <p>
              Questions about a product, an order, or the fountain pen hobby are
              welcome. ioomparade responds by email.
            </p>
            <a className="contact-details__email" href="mailto:globe@ioomparade.online">
              globe@ioomparade.online
            </a>

            <nav className="contact-details__links" aria-label="Common help topics">
              <Link to="/shipping">
                <span>Delivery information</span>
                <span aria-hidden="true">&#8594;</span>
              </Link>
              <Link to="/returns">
                <span>Returns and refunds</span>
                <span aria-hidden="true">&#8594;</span>
              </Link>
            </nav>
          </aside>

          <section className="contact-form-card" aria-labelledby="contact-form-title">
            <p className="ink-info-page__kicker">Send an email</p>
            <h2 id="contact-form-title">Prepare your message</h2>
            <p className="contact-form-card__intro">
              This form checks your details and prepares an email in your own
              mail app. It does not send or store your information on this site.
            </p>

            <form className="contact-form" noValidate onSubmit={handleSubmit}>
              <div className="contact-form__field">
                <label htmlFor="contact-name">
                  Name <span aria-hidden="true">*</span>
                </label>
                <input
                  autoComplete="name"
                  id="contact-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "contact-name-error" : undefined}
                />
                {renderFieldError("name")}
              </div>

              <div className="contact-form__field">
                <label htmlFor="contact-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  autoComplete="email"
                  id="contact-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "contact-email-error" : undefined}
                />
                {renderFieldError("email")}
              </div>

              <div className="contact-form__field">
                <div className="contact-form__label-row">
                  <label htmlFor="contact-message">
                    Message <span aria-hidden="true">*</span>
                  </label>
                  <span>{formData.message.length} characters</span>
                </div>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="7"
                  value={formData.message}
                  onChange={handleChange}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? "contact-message-error" : undefined}
                />
                {renderFieldError("message")}
              </div>

              <button className="ink-info-page__button" type="submit">
                Prepare email
              </button>

              {isReady && (
                <div className="contact-form__ready" role="status">
                  <strong>Your message is ready.</strong>
                  <p>
                    Nothing has been sent yet. Open your email app to review and
                    send it to ioomparade.
                  </p>
                  <a href={emailHref}>Open email app</a>
                </div>
              )}
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}

export default Contact;
